<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { profileDD } from '@/_mockApis/headerData';
import { useChangeProfileStore } from '@/shared/stores/changeProfileStore';
import { useDialog } from '@/shared/composables/useDialog';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { useTranslation } from '@/utils/locales/messages';
import defaultUserImage from '@/assets/images/profile/user-1.jpg';
import { checkMenuViewPermission } from '@/shared/utils/permissionUtils';
import { shouldApplyDeptMenuVisibilityForJurisdiction } from '@/shared/utils/deptMenuVisibility';
import { useInboxStore } from '@/features/inbox/stores/inboxStore';
import { isInboxFeatureEnabled } from '@/features/inbox/config/inboxFeature';

const sessionStore = useSessionStore();
const authStore = useAuthStore();
const inboxStore = useInboxStore();
const changeProfileStore = useChangeProfileStore();
const { warning } = useDialog();
const router = useRouter();
const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();

// Change Profile 메뉴 표시 여부 (IS_MULTI_CSP=1 회사만) — SidebarProfile 과 요청 공유
onMounted(() => {
    void changeProfileStore.loadStatus({ refresh: true });
});

const t = computed(() => createSyncT(languageStore.currentLanguage));
const newBadgeLabel = computed(() => t.value('common.inbox.newBadge') || 'NEW');

// 세션에서 사용자 정보 가져오기
const userName = computed(() => {
    const user = sessionStore.user;
    const userInfo = sessionStore.getUserInfo();
    const t = createSyncT(languageStore.currentLanguage);
    return userInfo?.name || user?.customer?.name || t('menu.user') || 'User';
});

const userEmail = computed(() => {
    const userInfo = sessionStore.getUserInfo();
    const user = sessionStore.user;
    // 백엔드에서 userEmail로 전달하는 경우도 처리
    return userInfo?.email || (userInfo as any)?.userEmail || user?.user?.email || (user?.user as any)?.userEmail || '';
});

// 프로필 이미지 URL (세션의 userImage 사용)
const userProfileImage = computed(() => {
    const userInfo = sessionStore.getUserInfo();
    const imageUrl = userInfo?.userImage;

    // 세션에 이미지가 있는 경우
    if (imageUrl && typeof imageUrl === 'string' && imageUrl.length > 0) {
        // Data URI, 절대 URL, 상대 경로 모두 처리
        if (imageUrl.startsWith('data:')) {
            // Data URI (이미지 바이너리)
            // Data URI 형식 검증: data:[<mediatype>][;base64],<data>
            const dataUriMatch = imageUrl.match(/^data:([^;]+)?(;base64)?,(.+)$/);
            if (dataUriMatch && dataUriMatch[3] && dataUriMatch[3].length > 0) {
                return imageUrl;
            } else {
                // Data URI 형식이지만 유효하지 않음
                return defaultUserImage;
            }
        } else if (imageUrl.startsWith('/uploads/')) {
            // 상대 경로인 경우 절대 경로로 변환
            return `${window.location.origin}${imageUrl}`;
        } else if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
            // 이미 절대 URL인 경우
            return imageUrl;
        } else {
            // 기타 형식 (예: 상대 경로지만 /uploads/로 시작하지 않는 경우)
            // 기본 이미지 반환
            return defaultUserImage;
        }
    }
    // 기본 이미지 (세션에 이미지가 없거나 비정상인 경우)
    return defaultUserImage;
});

// 이미지 로드 실패 시 기본 이미지로 대체
const handleImageError = (event: Event) => {
    const img = event.target as HTMLImageElement;
    // 기본 이미지가 아닌 경우에만 변경 (무한 루프 방지)
    if (img.src !== defaultUserImage) {
        const userInfo = sessionStore.getUserInfo();
        const imageUrl = userInfo?.userImage;
        console.warn('[VerticalSidebar] 프로필 이미지 로드 실패, 기본 이미지로 대체', {
            failedSrc: img.src,
            sessionImageUrl: imageUrl,
            sessionImageUrlType: typeof imageUrl,
            sessionImageUrlLength: typeof imageUrl === 'string' ? imageUrl.length : 0,
            sessionImageUrlPreview: typeof imageUrl === 'string' ? imageUrl.substring(0, 100) : imageUrl,
            userInfoKeys: userInfo ? Object.keys(userInfo) : []
        });
        img.src = defaultUserImage;
    }
};

// 다국어 처리된 profileDD (반응형으로 언어 변경 감지)
const translatedProfileDD = computed(() => {
    const t = createSyncT(languageStore.currentLanguage);

    const inboxEnabled = isInboxFeatureEnabled();

    const mapped = profileDD
        .filter((item) => inboxEnabled || item.href !== '/pages/inbox')
        .map(item => {
        let translatedTitle = item.title;
        const titleMap: Record<string, string> = {
            'My Subscription': t('menu.mySubscription') || 'My Subscription',
            'Account Settings': t('menu.accountSettings') || 'Account Settings',
            'Billing Settings': t('menu.Billing Settings') || 'Billing Settings',
            'Authorization Settings': t('menu.authorizationSettings') || 'Authorization Settings',
            'Users': t('menu.Users') || 'Users',
            'Inbox': t('menu.inbox') || 'Inbox',
            'Sign Out': t('menu.signOut') || 'Sign Out'
        };
        if (titleMap[item.title]) {
            translatedTitle = titleMap[item.title];
        }
        return { ...item, title: translatedTitle };
    });

    // Change Profile — Users 다음(없으면 Sign Out 앞, 그것도 없으면 맨 끝). href 없는 항목이라 권한 필터를 통과함
    if (changeProfileStore.enabled) {
        const usersIdx = mapped.findIndex(i => i.href === '/users');
        const logoutIdx = mapped.findIndex(i => i.href === '/auth/logout');
        const at = usersIdx >= 0 ? usersIdx + 1 : (logoutIdx >= 0 ? logoutIdx : mapped.length);
        mapped.splice(at, 0, { title: t('menu.changeProfile') || 'Change Profile', href: '', badge: false, action: 'changeProfile' });
    }

    // 세션·관할 확인 (필터 적용 전, MY/SG 한정 아님)
    const customerInfo = sessionStore.user?.customer || null;
    const jurisdiction = String(customerInfo?.jurisdiction || '').toUpperCase();
    const shouldApplyDeptMenuVisibility = shouldApplyDeptMenuVisibilityForJurisdiction(jurisdiction);
    if (!shouldApplyDeptMenuVisibility) return mapped;

    const perms = sessionStore.getPermissions as any;
    const accountTitle = t('menu.accountSettings') || 'Account Settings';
    const authzTitle = t('menu.authorizationSettings') || 'Authorization Settings';

    const inboxTitle = t('menu.inbox') || 'Inbox';

    return mapped.filter(item => {
        if (!item.href || item.href === '/auth/logout') return true;
        if (item.title === accountTitle) return true;
        if (item.href === '/pages/inbox' || item.title === inboxTitle) return true;
        if (item.title === authzTitle) {
            return checkMenuViewPermission(perms, '/pages/authorization-settings');
        }
        return checkMenuViewPermission(perms, item.href);
    });
});

// 메뉴 클릭 처리
const handleMenuClick = async (item: { href?: string; title?: string; action?: string }) => {
    if (item.action === 'changeProfile') {
        changeProfileStore.open();
        return;
    }
    // CSP 프로필로 전환한 상태에서는 원래 회사 전용 메뉴로 이동하지 않고 안내(C69)
    if (changeProfileStore.isOwnerOnlyBlocked(item.href)) {
        await warning(
            t.value('menu.changeProfile') || 'Change Profile',
            (t.value('menu.changeProfileOwnerOnly') || 'This menu is only available from {companyName}. Switch back to {companyName} in Change Profile.')
                .replace(/\{companyName\}/g, changeProfileStore.ownerName || t.value('menu.changeProfileOwner') || 'Your company')
        );
        return;
    }
    if (item.href === '/auth/logout') {
        await authStore.logout();
        router.push('/auth/login');
    }
};

const onProfileMenuToggle = (isOpen: boolean) => {
    if (isOpen) {
        inboxStore.menuOpen = false;
    }
};

// 템플릿에서 사용할 번역 함수 (반응형)
const userAltText = computed(() => {
    const t = createSyncT(languageStore.currentLanguage);
    return t('menu.user') || 'user';
});
</script>

<template>
    <!-- ---------------------------------------------- -->
    <!-- notifications DD -->
    <!-- ---------------------------------------------- -->
    <v-menu
        open-on-click
        class="profile_popup"
        :z-index="2700"
        location="bottom end"
        @update:model-value="onProfileMenuToggle"
    >
        <template v-slot:activator="{ props }">
            <v-btn variant="text" v-bind="props" icon size="small" color="primary" class="custom-hover-primary ms-md-2">
                <v-avatar size="30">
                    <img :src="userProfileImage" width="30" :alt="userAltText" @error="handleImageError" />
                </v-avatar>
            </v-btn>
        </template>
        <v-sheet rounded="md" width="350" elevation="10">
            <div class="pb-4">
                <div class="d-flex align-center pa-4">
                    <v-avatar size="50">
                        <img :src="userProfileImage" width="50" :alt="userName" @error="handleImageError" />
                    </v-avatar>
                    <div class="ml-3">
                        <h6 class="text-h6  textPrimary font-weight-medium">{{ userName }}</h6>
                        <span class="text-12 textSecondary">{{ userEmail }}</span>
                    </div>
                </div>
                <v-divider></v-divider>
                <perfect-scrollbar style="height: calc(100vh - 240px); max-height: 240px">
                <v-list class="py-0 px-4 theme-list mt-3" lines="one">
                    <v-list-item
                        v-for="item in translatedProfileDD"
                        :key="item.title"
                        class="py-0 px-2 rounded-md custom-text-primary"
                        color="primary"
                        :to="item.href && item.href !== '/auth/logout' && !changeProfileStore.isOwnerOnlyBlocked(item.href) ? item.href : undefined"
                        @click="item.href === '/auth/logout' || item.action || changeProfileStore.isOwnerOnlyBlocked(item.href) ? handleMenuClick(item) : undefined"
                    >
                        <div class="d-flex gap-3 align-center justify-space-between w-100">
                            <p class="text-subtitle-1 textPrimary hover-primary font-weight-medium mb-0">
                                {{ item.title }}
                            </p>
                            <v-chip
                                v-if="item.href === '/pages/inbox' && inboxStore.unreadCount > 0"
                                size="x-small"
                                color="error"
                                variant="flat"
                                label
                                class="profile-inbox-new"
                            >
                                {{ newBadgeLabel }}
                            </v-chip>
                            <v-chip v-else-if="item.badge" size="small" color="info">4</v-chip>
                        </div>
                    </v-list-item>
                </v-list>
            </perfect-scrollbar>
            </div>
        </v-sheet>
    </v-menu>
</template>
